import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import keycloak from '../services/keycloak';
import secureLocalStorage from '../utils/secureStorage';
import { provisionNiaUser } from '../services/niaProvisioningService';
import { licenseService } from '../services/licenseManagerService';

interface User {
  username: string;
  displayName: string;
  email: string;
  roles: string[];
}

interface AuthContextType {
  authenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasAcceptedTerms: boolean;
  setHasAcceptedTerms: (value: boolean) => void;
  lastLoginAt: string | null;
  menuPermissions: Record<string, boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAcceptedTerms, setHasAcceptedTermsState] = useState<boolean>(() => secureLocalStorage.getItem('hasAcceptedTerms') === 'true');
  const [lastLoginAt, setLastLoginAt] = useState<string | null>(() => secureLocalStorage.getItem('lastLoginAt') as string | null);

  const [menuPermissions, setMenuPermissions] = useState<Record<string, boolean>>(() => {
    return { showSwob: false, showNii: false, showKpi: true };
  });

  const extractUser = useCallback((): User | null => {
    if (!keycloak.tokenParsed) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tp = keycloak.tokenParsed as any;
    const keycloakRoles: string[] = tp.realm_access?.roles || [];
    const appRoles = keycloakRoles.filter((r: string) => ['admin', 'user'].includes(r));
    if (appRoles.length === 0) appRoles.push('user');
    return {
      username: tp.preferred_username || '',
      displayName: tp.displayName || tp.name || tp.preferred_username || '',
      email: tp.email || '',
      roles: appRoles
    };
  }, []);

  const getLicenses = async () => {
    try {
      const { swob, nii, kpi } = await licenseService.getLicenses();
      setMenuPermissions({ showSwob: swob, showNii: nii, showKpi: kpi });
    } catch (error) {
      console.log(`license-manager unavailable, showing all menus: ${error}`);
      setMenuPermissions({ showSwob: true, showNii: true, showKpi: true });
    }
  };

  useEffect(() => {
    keycloak
      .init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false
      })
      .then((auth) => {
        setAuthenticated(auth);
        if (auth) {
          setToken(keycloak.token || null);
          const extractedUser = extractUser();
          setUser(extractedUser);
          const now = new Date().toISOString();
          secureLocalStorage.setItem('lastLoginAt', now);
          setLastLoginAt(now);
          // Provision NIA user and store token for embedded apps (iframes)
          if (extractedUser?.email) {
            provisionNiaUser(extractedUser.email);
          }
          getLicenses();
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Keycloak init failed:', err);
        setLoading(false);
      });

    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken(30)
        .then((refreshed) => {
          if (refreshed) {
            setToken(keycloak.token || null);
            setUser(extractUser());
          }
        })
        .catch(() => keycloak.login());
    };

    keycloak.onAuthRefreshSuccess = () => {
      setToken(keycloak.token || null);
      setUser(extractUser());
    };
  }, [extractUser]);

  const login = useCallback(() => keycloak.login(), []);

  const logout = useCallback(() => {
    secureLocalStorage.removeItem('hasAcceptedTerms');
    secureLocalStorage.removeItem('lastLoginAt');
    secureLocalStorage.removeItem('hasShownLoginNotification');
    secureLocalStorage.removeItem('swob-inActive-expireTime');
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  const hasRole = useCallback((role: string) => user?.roles.includes(role) || false, [user]);

  const setHasAcceptedTerms = useCallback((value: boolean) => {
    secureLocalStorage.setItem('hasAcceptedTerms', String(value));
    setHasAcceptedTermsState(value);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        user,
        token,
        loading,
        login,
        logout,
        hasRole,
        hasAcceptedTerms,
        setHasAcceptedTerms,
        lastLoginAt,
        menuPermissions
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export default AuthProvider;
